export type IntroPhase = 'idle' | 'sweeping' | 'done'

const DURATION = 4 // seconds

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t)
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

// Start: elevated overview looking across desk rows
const START_POS       = { x: 0, y: 14, z: 30 }
const START_PITCH_DEG = -28
const START_YAW_DEG   = 0

// End: player spawn at eye level
const END_POS       = { x: 0, y: 1.75, z: 8 }
const END_PITCH_DEG = 0
const END_YAW_DEG   = 0

export interface SweepFrame {
  pos: { x: number; y: number; z: number }
  pitchDeg: number
  yawDeg: number
}

export class IntroSweep {
  private phase: IntroPhase = 'idle'
  private elapsed = 0

  get isIdle(): boolean    { return this.phase === 'idle' }
  get isSweeping(): boolean { return this.phase === 'sweeping' }
  get isDone(): boolean    { return this.phase === 'done' }

  start(): void {
    if (this.phase !== 'idle') return
    this.phase   = 'sweeping'
    this.elapsed = 0
    window.addEventListener('keydown',    this.skip, { once: true })
    document.addEventListener('mousedown', this.skip, { once: true })
  }

  readonly skip = (): void => {
    if (this.phase === 'sweeping') this.phase = 'done'
    window.removeEventListener('keydown',    this.skip)
    document.removeEventListener('mousedown', this.skip)
  }

  update(dt: number): SweepFrame | null {
    if (this.phase !== 'sweeping') return null
    this.elapsed += dt
    const raw = Math.min(this.elapsed / DURATION, 1)
    const e   = smoothstep(raw)

    if (raw >= 1) {
      this.phase = 'done'
      window.removeEventListener('keydown',    this.skip)
      document.removeEventListener('mousedown', this.skip)
    }

    return {
      pos: {
        x: lerp(START_POS.x, END_POS.x, e),
        y: lerp(START_POS.y, END_POS.y, e),
        z: lerp(START_POS.z, END_POS.z, e),
      },
      pitchDeg: lerp(START_PITCH_DEG, END_PITCH_DEG, e),
      yawDeg:   lerp(START_YAW_DEG,   END_YAW_DEG,   e),
    }
  }

  destroy(): void {
    window.removeEventListener('keydown',    this.skip)
    document.removeEventListener('mousedown', this.skip)
  }
}
